// PistolSkill.js - Manages pistol leveling and attribute scaling

/**
 * PistolSkill class
 * Tracks pistol XP, levels, and attribute scaling
 * Centralized location for all pistol upgrade logic
 */
export class PistolSkill {
  constructor() {
    console.log("PistolSkill constructor called");
    
    // Base stats at level 1
    this.level = 1;
    this.xp = 0;
    this.xpToNextLevel = 50; // XP needed for first level up
    
    // Base attributes (level 1)
    this.baseReloadTime = 2000; // 2 seconds reload time
    this.baseDamage = 15;
    this.magazineSize = 6;
    this.baseProjectileSpeed = 120; // Doubled base speed (was 60)
    
    // Scaling factors per level
    this.reloadTimeReductionPerLevel = 0.1; // 10% faster reload per level
    this.damageIncreasePerLevel = 5; // +5 damage per level
    this.projectileSpeedIncreasePerLevel = 5; // +5 speed per level
    
    // DEBUG: Track XP sources
    this.xpSources = {
      zombieKills: 0,
      debugXp: 0
    };
    
    // Make globally available for debugging and integration
    window.pistolSkill = this;
    console.log("PistolSkill initialized at level 1");
    
    // Update inventory after a short delay to ensure everything is loaded
    setTimeout(() => this.updateInventoryDisplay(), 1000);
  }
  
  /**
   * Get modified reload time based on current level
   * @returns {number} Reload time in milliseconds
   */
  getReloadTime() {
    const reloadModifier = Math.max(0.5, 1 - ((this.level - 1) * this.reloadTimeReductionPerLevel));
    return this.baseReloadTime * reloadModifier;
  }
  
  /**
   * Get reload time modifier (used by ReloadUI)
   * @returns {number} Modifier value (1.0 = normal, 0.5 = twice as fast)
   */
  getReloadTimeModifier() {
    return Math.max(0.5, 1 - ((this.level - 1) * this.reloadTimeReductionPerLevel));
  }
  
  /**
   * Get damage based on current level
   * @returns {number} Damage value
   */
  getDamage() {
    return this.baseDamage + ((this.level - 1) * this.damageIncreasePerLevel);
  }
  
  /**
   * Get magazine size based on current level
   * @returns {number} Magazine size
   */
  getMagazineSize() {
    if (this.level <= 5) {
      // Up to level 5: +1 every 2 levels
      const magazineBonus = Math.floor((this.level - 1) / 2);
      return this.magazineSize + magazineBonus;
    } else {
      // After level 5: +2 per level
      const bonusUpToLevel5 = Math.floor((5 - 1) / 2); // Bonus from levels 1-5
      const bonusAfterLevel5 = (this.level - 5) * 2;   // +2 per level after 5
      return this.magazineSize + bonusUpToLevel5 + bonusAfterLevel5;
    }
  }
  
  /**
   * Get current projectile speed based on level
   * @returns {number} Projectile speed value
   */
  getProjectileSpeed() {
    return this.baseProjectileSpeed + (this.level - 1) * this.projectileSpeedIncreasePerLevel;
  }
  
  /**
   * Add XP and handle level-ups
   * @param {number} amount - Amount of XP to add
   * @param {string} source - Source of XP (for tracking)
   * @returns {boolean} True if level up occurred
   */
  addXP(amount, source = 'unknown') {
    this.xp += amount;
    
    // Track XP source
    if (source === 'zombieKill') {
      this.xpSources.zombieKills += amount;
    } else if (source === 'debug') {
      this.xpSources.debugXp += amount;
    }
    
    console.log(`Pistol gained ${amount}XP from ${source}, total: ${this.xp}/${this.xpToNextLevel}`);
    
    // Create floating XP text
    this.showXpGain(amount);
    
    // Check for level up
    if (this.xp >= this.xpToNextLevel) {
      this.levelUp();
      return true;
    }
    return false;
  }
  
  /**
   * Handle level up
   */
  levelUp() {
    this.level++;
    this.xp -= this.xpToNextLevel;
    this.xpToNextLevel = Math.floor(this.xpToNextLevel * 1.5); // Increase XP needed for next level
    
    console.log(`🔫 Pistol leveled up to ${this.level}!`);
    console.log(`- New reload time: ${this.getReloadTime()}ms`);
    console.log(`- New damage: ${this.getDamage()}`);
    console.log(`- Magazine size: ${this.getMagazineSize()}`);
    
    // Show level up notification
    this.showLevelUpMessage();
    
    // Special notification for level 5 (full auto unlock)
    if (this.level === 5) {
      this.showFullAutoUnlockMessage();
    }
    
    // Update reload UI if available
    if (window.reloadUI && typeof window.reloadUI.setReloadTimeModifier === 'function') {
      window.reloadUI.setReloadTimeModifier(this.getReloadTimeModifier());
    }
    
    // Broadcast a custom event that the inventory can listen for
    const levelUpEvent = new CustomEvent('pistol-level-up', { 
      detail: { level: this.level, source: 'PistolSkill' } 
    });
    window.dispatchEvent(levelUpEvent);
    console.log('PistolSkill: Dispatched pistol-level-up event with level:', this.level);
    
    // Reset xp to remainder and check for multiple level ups
    if (this.xp >= this.xpToNextLevel) {
      this.levelUp();
    }
  }
  
  /**
   * Show level up message
   */
  showLevelUpMessage() {
    // Get old and new stats
    const oldDamage = this.baseDamage + ((this.level - 2) * this.damageIncreasePerLevel);
    const newDamage = this.getDamage();
    const oldReloadTime = (this.baseReloadTime * Math.max(0.5, 1 - ((this.level - 2) * this.reloadTimeReductionPerLevel))).toFixed(0);
    const newReloadTime = this.getReloadTime().toFixed(0);
    const oldMagazine = this.magazineSize + Math.floor((this.level - 2) / 2);
    const newMagazine = this.getMagazineSize();

    // Create level up notification
    const div = document.createElement('div');
    div.className = 'level-up';
    div.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 10px;">Pistol Level ${this.level}!</div>
      <div style="font-size: 16px; text-align: left;">
        Damage: ${oldDamage} → <span style="color: #00ff00">${newDamage}</span><br>
        Reload: ${oldReloadTime}ms → <span style="color: #00ff00">${newReloadTime}ms</span><br>
        Magazine: ${oldMagazine} → <span style="color: #00ff00">${newMagazine}</span>
      </div>
    `;
    div.style.cssText = `
      position: fixed;
      top: 30%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      font-weight: bold;
      text-align: center;
      animation: pulse 2s ease-out;
      pointer-events: none;
      z-index: 1000;
      min-width: 250px;
    `;
    document.body.appendChild(div);
    
    // Remove after animation
    setTimeout(() => {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    }, 3000);
  }
  
  /**
   * Show full auto unlock message
   */
  showFullAutoUnlockMessage() {
    const div = document.createElement('div');
    div.className = 'level-up';
    div.innerHTML = `
      <div style="font-size: 24px; margin-bottom: 10px;">🔫 Full Auto Unlocked! 🔫</div>
      <div style="font-size: 16px; text-align: center;">
        Hold left click to fire continuously!
      </div>
    `;
    div.style.cssText = `
      position: fixed;
      top: 40%;
      left: 50%;
      transform: translate(-50%, -50%);
      background-color: rgba(0, 0, 0, 0.9);
      color: white;
      padding: 15px 20px;
      border-radius: 8px;
      font-weight: bold;
      text-align: center;
      animation: pulse 2s ease-out;
      pointer-events: none;
      z-index: 1001;
      min-width: 300px;
      text-shadow: 0 0 10px #ffdd44;
    `;
    document.body.appendChild(div);
    
    // Remove after animation
    setTimeout(() => {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    }, 4000); // Show for 4 seconds since it's an important unlock
  }
  
  /**
   * Update the inventory display to show current pistol level
   */
  updateInventoryDisplay() {
    // Debug output to track level updates
    console.log(`PistolSkill: Updating inventory UI with level ${this.level}`);
    
    // Find player controller and update inventory UI if available
    if (window.playerController && 
        window.playerController.inventory &&
        typeof window.playerController.inventory.updateUI === 'function') {
      console.log('PistolSkill: Found inventory, calling updateUI()');
      window.playerController.inventory.updateUI();
    } else {
      console.warn('PistolSkill: Could not update inventory UI - missing playerController or inventory');
    }
  }
  
  /**
   * Show floating XP gain text
   * @param {number} amount - Amount of XP gained
   */
  showXpGain(amount) {
    // Create a temporary div for XP notification
    const div = document.createElement('div');
    div.className = 'xp-gain';
    div.textContent = `+${amount} XP`;
    div.style.cssText = `
      position: fixed;
      bottom: 100px;
      right: 20px;
      background-color: rgba(0, 100, 255, 0.7);
      color: white;
      padding: 8px 12px;
      border-radius: 4px;
      font-weight: bold;
      animation: float-up 2s ease-out forwards;
      pointer-events: none;
      z-index: 1000;
    `;
    document.body.appendChild(div);
    
    // Remove after animation
    setTimeout(() => {
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    }, 2000);
  }
  
  /**
   * DEBUG: Force level up (for testing)
   */
  debugLevelUp() {
    this.levelUp();
    return this.level;
  }
  
  /**
   * DEBUG: Force add XP (for testing)
   * @param {number} amount - Amount of XP to add
   */
  debugAddXP(amount) {
    return this.addXP(amount, 'debug');
  }
  
  /**
   * Get pistol stats summary (for UI/debugging)
   * @returns {Object} Object containing all relevant stats
   */
  getStats() {
    return {
      level: this.level,
      xp: this.xp,
      xpToNextLevel: this.xpToNextLevel,
      reloadTime: this.getReloadTime(),
      damage: this.getDamage(),
      magazineSize: this.getMagazineSize(),
      xpSources: this.xpSources
    };
  }
}

// Create a single instance when the module is loaded
const pistolSkill = new PistolSkill();

// Animations are now in style.css, no need to add them here
// Removing the style creation to avoid duplicates

export default pistolSkill; 